/**
 * Quick entry: the inline "Item Number / Quantity (UOM) / Price (UOM) / Add Below" row that lets a
 * customer find a product by SKU, part number or text and add it straight to the cart, a shopping
 * list or a draft quote without leaving the page. This is the quick entry every screen renders;
 * `QuickEntry.tsx` is an older copy that no screen renders, and is to be deleted.
 *
 * Position: rendered by `ShoppingCart/ShoppingCartDesktop.tsx` and `ShoppingCartMobile.tsx`,
 * `ShoppingListDetails/Helpers/ShoppingListDetailsDesktopContent.tsx` and
 * `ShoppingLIstMobileContent.tsx`, and `QuoteDetails/QuoteDetails.tsx` (draft quote in edit mode).
 * This file holds the state and the API calls; `Helpers/V2/QuickEntryDesktopV2.tsx` and
 * `Helpers/V2/QuickEntryMobileV2.tsx` render it, split at the `lg` breakpoint, through the
 * `QuickEntryProps` contract in `Helpers/QuickEntry.models.tsx`.
 *
 * Calls:
 * - Search: `GET APIUrl.SEARCH_PRODUCTS` (`web-items-api` `public/items`) with `search-phrase` and
 *   `zip-code` (`AccountSettings/Helpers/zipCode.ts` → `getZipCode`), once the term has stopped
 *   changing for 1 s and is at least `SEARCH_MIN_LENGTH` (`Search/Helpers/Search.models.ts`) long.
 * - Cart: `addItemToCart` from `useEcommerce()` (`@ryerson/frontend.e-commerce`), which posts to
 *   the Shopping API when signed in and appends to the package's guest cart otherwise.
 * - Shopping list: `Helper/shoppingListHelper.ts` → `addProductsToShoppingList`.
 * - Draft quote: `QuoteDetails/Helpers/HelperFunctions.tsx` → `addLineItemAPI`
 *   (`POST` `APIUrl.DRAFT_QUOTE` `/<quote id>/item`, `web-quotes-api`).
 *
 * Rules a change must keep:
 * - Every caller imports `QuickEntryDataType` from `QuickEntry.tsx`, not from this file, and
 *   passes it here. The list-details and quote-details callers also import `SetQuickEntryData`
 *   from there; the cart callers pass a no-op `setData`. TypeScript accepts V1's enum here only
 *   because the two `QuickEntryDataType` enums have the same name and the same members, so they
 *   change together. Removing `QuickEntry.tsx` means moving those imports to this file first.
 * - Only the cart path sends the GA `ADD_TO_CART` event.
 *
 * Failure handling differs by target: a failed draft-quote add shows "not added below"; a failed
 * cart add (no `onFailure` is passed to `addItemToCart`) or shopping-list add (the helper only
 * logs) shows nothing.
 */
import { useApplication } from "@ryerson/frontend.application";
import React, { useEffect, useRef, useState } from "react";
import { APIUrl } from "@enums/apiUrl.enums";
import {
	useDebounce,
	useOnClickOutside,
	useCookie,
	getCustomerPartNumber,
	Subscribable,
	getUsersDefaultUomValue,
	ShoppingCartDataType,
	DraftQuoteLineItem,
	sendGAEvent,
	GoogleAnalyticsEventName,
	EventParams,
	EventParamsItem,
} from "@ryerson/frontend.common";
import { Product } from "@ryerson/frontend.common";
import { Media } from "@ryerson/frontend.next";
import { getZipCode } from "@components/AccountSettings/Helpers/zipCode";

import { SEARCH_MIN_LENGTH } from "@components/Search/Helpers/Search.models";
import { useEcommerce } from "@ryerson/frontend.e-commerce";

import {
	addProductsToShoppingList,
	ShoppingListProduct,
} from "@components/Helper/shoppingListHelper";
import { ShoppingListItem } from "@ryerson/frontend.common";
import { getItem } from "@components/Hooks";
import { lb, lbs, pcs } from "@ryerson/common.uom";
import { addLineItemAPI } from "@components/QuoteDetails/Helpers/HelperFunctions";
import { defaultPricingData } from "@components/ShoppingCart/ShoppingCart.model";

import { Form } from "@ryerson/common.process-cut";
import { isAxiosError } from "@components/Helper/TypeHelpers";
import { AxiosResponse } from "axios";
import { QuickEntryProps } from "./Helpers/QuickEntry.models";
import QuickEntryMobileV2 from "./Helpers/V2/QuickEntryMobileV2";
import QuickEntryDesktopV2 from "./Helpers/V2/QuickEntryDesktopV2";

/**
 * Which collection an added item goes to. Mirrors `QuickEntry.tsx` → `QuickEntryDataType`, which
 * is the one callers import; keep the name and members identical (see the file header).
 */
export enum QuickEntryDataType {
	ShoppingCart = 1,
	ShoppingList = 2,
	QuoteList = 3,
}

/**
 * One row of whichever collection the quick entry adds to: a cart line, list item or quote line.
 */
type QuickEntryData = ShoppingCartDataType | ShoppingListItem | DraftQuoteLineItem;
/** State setter for the caller's rows; receives the previous rows plus the added one. */
export type SetQuickEntryData = React.Dispatch<React.SetStateAction<QuickEntryData[]>>;
/* 	| React.Dispatch<React.SetStateAction<ShoppingListItem[]>>
	| React.Dispatch<React.SetStateAction<DraftQuoteLineItem[]>>; */

/** Props of {@link QuickEntryV2}. */
export type QuickEntryTypeV2 = {
	dataType: QuickEntryDataType;
	/** The caller's current rows; the added row is appended to a copy of these. */
	data: QuickEntryData[];
	/**
	 * Receives the new rows after a successful add. The cart callers pass a no-op and rely on
	 * `updateShoppingCart`.
	 */
	setData: SetQuickEntryData;
	showQuickEntry: boolean;
	/** Called with `false` by the close icon. */
	setShowQuickEntry: React.Dispatch<React.SetStateAction<boolean>>;
	/** Cart only: reset to `defaultPricingData` after an add. */
	setPricingOfItems?: (value: any) => void;
	/** Shopping-list id or draft-quote id; unused for the cart. */
	id?: any;
	/**
	 * Quote only: the item number of the last draft line. The added line gets this plus 1, or 1
	 * when absent.
	 */
	itemNumber?: number | undefined;
	/** Cart only: reloads the cart after an add. */
	updateShoppingCart?: () => void;
};

/** A UOM dropdown option (`@ryerson/common.uom` values such as `lbs`, `pcs`). */
export interface DropdownOption {
	display: string;
	value: string;
}

/**
 * Quick entry for the cart, a shopping list or a draft quote, chosen by `dataType`. See the file
 * header for the calls it makes and the rules it keeps.
 */
const QuickEntryV2: React.FC<QuickEntryTypeV2> = ({
	dataType,
	data,
	setData,
	showQuickEntry,
	setShowQuickEntry,
	setPricingOfItems,
	id,
	itemNumber,
	updateShoppingCart,
}) => {
	let debouncedSearchInput: string;

	let NOTIFY_TIMEOUT = 5000;

	// Use default option until API get ready
	// `searchTerm` drives the search; `displaySearchTerm` is what the input shows. Picking a
	// product shows its `fastPath` without changing `searchTerm`, so no second search runs.
	const [searchTerm, setSearchTerm] = useState<string | number>("");
	const [displaySearchTerm, setDisplaySearchTerm] = useState<string | number>("");
	const [showDropdown, setShowDropdown] = useState<boolean>(false);
	const [isError, setIsError] = useState<boolean>(false);
	const [searchResult, setSearchResult] = useState<Array<Product>>([]);
	const [selectedProduct, setSelectedProduct] = useState<Product | undefined>();
	const [quantity, setQuantity] = useState<number | undefined>();
	const [qtyUOM, setQtyUOM] = useState<string>(lbs.value);
	const [qtyUOMDropdown, setQtyUOMDropdown] = useState<DropdownOption[]>([lbs]);
	const [priceUOM, setPriceUOM] = useState<string>(lb.value);
	const [priceUOMDropdown, setPriceUOMDropdown] = useState<DropdownOption[]>([lb]);
	const [itemAddSuccess, setItemAddSuccess] = useState(false);
	const [itemAddFail, setItemAddFail] = useState(false);
	const [, setShoppingListData] = useCookie("addToShoppingList", "");

	const searchInputContainerRef = useRef<HTMLElement>(null);
	const searchResultsRef = useRef<HTMLElement>(null);
	const [hasMounted, setHasMounted] = useState<boolean>(false);

	// added to support new quantityUOM Component
	const [isQuickEntrySubmitted, setIsQuickEntrySubmitted] = React.useState<boolean>(false);
	const [isQuickEntryValid, setIsQuickEntryValid] = React.useState<boolean>(false);

	// PUB-SUB: Instantiated Subscribable , in this case calling it stateMessenger
	const [stateMessenger] = React.useState<Subscribable<string>>(new Subscribable<string>());

	useOnClickOutside([searchInputContainerRef, searchResultsRef], () => {
		setShowDropdown(false);
	});

	const { setCartPricingData, addItemToCart, selectedShipToAddress, customerPickup, salesPlant } =
		useEcommerce();

	const {
		axiosInstance,
		zipCode,
		user: { isLoggedIn },
		profile,
	} = useApplication();

	useEffect(() => {
		if (itemAddSuccess) {
			setTimeout(() => {
				setItemAddSuccess(false);
			}, NOTIFY_TIMEOUT);
		}
		if (itemAddFail) {
			setTimeout(() => {
				setItemAddFail(false);
			}, NOTIFY_TIMEOUT);
		}
	}, [itemAddSuccess, itemAddFail]);

	const DEBOUNCE_TIME = 1000;

	debouncedSearchInput = searchTerm.toString();
	const debouncedSearchTerm = useDebounce(debouncedSearchInput, DEBOUNCE_TIME);

	const resetToDefault = () => {
		setSelectedProduct(undefined);
		setShowDropdown(false);
		setSearchResult([]);
		setIsError(false);
		setQuantity(undefined);

		setIsQuickEntrySubmitted(false);

		setQtyUOM(lbs.value);
		setPriceUOM(lb.value);
		setQtyUOMDropdown([lbs]);
		setPriceUOMDropdown([lb]);
	};

	/**
	 * True when the product must be entered in pieces only: it has no weight per foot, square foot
	 * or piece (and is not a coil), or its `cutFlag` is `"A"` (`DefineCut.CutIsAlwaysRequired` in
	 * `@ryerson/frontend.common`). Despite the name, false does not mean invalid.
	 *
	 * Business rule, not a quick-entry limitation: quick entry offers only "pieces" for
	 * cut-required products and products with no weight data. A change must keep it.
	 */
	const isProductDetailsValid = (selectedProduct: Product): boolean => {
		if (
			(!selectedProduct.weightPerFoot &&
				!selectedProduct.weightPerSquareFoot &&
				!selectedProduct.weightPerPiece &&
				selectedProduct.form !== Form.COIL) ||
			selectedProduct.cutFlag === "A"
		) {
			return true;
		} else {
			return false;
		}
	};

	const updateQuantityUom = (value: string) => {
		setQtyUOM(value);
	};

	// Any keystroke clears the selected product, quantity and UOMs; the search runs once the term
	// has settled. A settled term shorter than `SEARCH_MIN_LENGTH` does nothing. An empty result
	// and a request error both show "No Results Found".
	useEffect(() => {
		const searchTermLength = searchTerm?.toString().length;
		if (searchTermLength < 1 || debouncedSearchTerm !== searchTerm) {
			resetToDefault();
		} else if (searchTermLength >= SEARCH_MIN_LENGTH && debouncedSearchTerm === searchTerm) {
			let inputData = encodeURIComponent(searchTerm.toString().trim());
			let queryParams = `search-phrase=${inputData}`;

			queryParams += `&zip-code=${getZipCode(
				isLoggedIn,
				zipCode,
				selectedShipToAddress,
				customerPickup,
				salesPlant
			)}`;

			axiosInstance
				.get(`${APIUrl.SEARCH_PRODUCTS}?${queryParams}`)
				.then((response: any) => {
					let responseData = response?.data;
					if (responseData?.length > 0) {
						setSearchResult(responseData);
						setShowDropdown(true);
					} else {
						setIsError(true);
					}
				})
				.catch(() => {
					setIsError(true);
				});
		}
	}, [debouncedSearchTerm, searchTerm]);

	useEffect(() => {
		setHasMounted(true);
	}, []);

	// Quantity and price UOM defaults come from the product and the user's per-form UOM settings
	// (`profile.settings.UOM`), through `getUsersDefaultUomValue` in `@ryerson/frontend.common`.
	const setUOMAndPriceUOMDropDown = (productItem: Product): void => {
		const { defaultQtyUom, defaultPricingUom, pricingUomOption, qtyUomOption } =
			getUsersDefaultUomValue(productItem, profile);

		setQtyUOM(defaultQtyUom);
		setPriceUOM(defaultPricingUom);
		setPriceUOMDropdown(pricingUomOption);
		setQtyUOMDropdown(qtyUomOption);
	};

	const onClickSelectItem = (productItem: Product): void => {
		setUOMAndPriceUOMDropDown(productItem);
		setSelectedProduct(productItem);
		setDisplaySearchTerm(productItem.fastPath);
		setShowDropdown(false);
		setQuantity(undefined);
		if (isProductDetailsValid(productItem)) {
			setQtyUOM(pcs.value);
			setQtyUOMDropdown([pcs]);
		}
		setIsError(false);
	};

	const onChangeText = (event: any): void => {
		const val = event.target.value;

		setItemAddSuccess(false);
		setItemAddFail(false);
		setSearchTerm(val);
		setDisplaySearchTerm(val);
	};

	// Validation Response called by QuantityUOM  Component
	const handleValidationResponse = (hasError: boolean, id: number | string) => {
		setIsQuickEntryValid(!hasError);
	};

	/**
	 * Adds the selected product to the target collection. Does nothing until `QuantityUOMV2` has
	 * reported a valid (non-zero) quantity. `callBack` runs after the add; the desktop view uses it
	 * to return focus to the search input. The button is not disabled while the request is in
	 * flight.
	 */
	const onClickAddBelow = (callBack?: Function) => {
		setIsQuickEntrySubmitted(true);

		if (!isQuickEntryValid) {
			return false;
		}

		if (selectedProduct) {
			// Local row id for rows added without a server response (guest cart, guest list).
			// `quoteListLastIndex` is read here but never written.
			let lastIndex: number = Number(
				sessionStorage.getItem(
					dataType === QuickEntryDataType.ShoppingCart
						? "cartLastIndex"
						: dataType === QuickEntryDataType.ShoppingList
						? "shoppingListLastIndex"
						: "quoteListLastIndex"
				) || 0
			);
			let addToObject = {
				mm: selectedProduct?.materialMaster,
				qty: quantity,
				quom: qtyUOM,
				puom: priceUOM,
			};
			let newlyAddedDataObject: QuickEntryData = {
				id: lastIndex + 1,
				materialMaster: addToObject.mm,
				quantity: addToObject.qty || 0,
				quantityUOM: addToObject.quom,
				priceUOM: addToObject.puom,
				shoppingCartId: "",
				customerRefNumber: "",
				purchaseOrderNumber: "",
				notes: "",
				domesticRequired: false,
			};

			// Cart. Signed in: the Shopping API returns the saved lines, mapped to cart rows here.
			// Guest: the package appends the line to its guest cart and this adds a local row.
			if (dataType === QuickEntryDataType.ShoppingCart && quantity) {
				addItemToCart(
					axiosInstance,
					isLoggedIn,
					[
						{
							ryersonSku: selectedProduct.materialMaster,
							customerPartNumber: getCustomerPartNumber(
								selectedProduct?.altPartNumber
							),
							quantity: quantity,
							quantityUom: qtyUOM,
							pricingUom: priceUOM,
							customerRefNumber: "",
							purchaseOrderNumber: "",
							notes: "",
							domesticRequired: false,
						},
					],
					isLoggedIn
						? (response) => {
								const responseData = response?.data;
								if (responseData) {
									if (updateShoppingCart) {
										updateShoppingCart();
									}
									let responsedCartData = responseData.map(
										(item: any): QuickEntryData => {
											return {
												id: item.id,
												shoppingCartId: item.shoppingCartId,
												materialMaster: item.ryersonSku,
												partNumber: item.customerPartNumber,
												quantity: item.quantity,
												quantityUOM: item.quantityUom,
												priceUOM: item.pricingUom,
												customerRefNumber: item.customerRefNumber,
												purchaseOrderNumber: item.purchaseOrderNumber,
												notes: item.notes,
												domesticRequired: item.domesticRequired,
											};
										}
									);
									let allCartItems = [...data, ...responsedCartData];

									callBack && callBack();
									handleOnSuccessAddItem(allCartItems);
								}
								/* eslint-disable-next-line */
						  }
						: () => {
								sessionStorage.setItem("cartLastIndex", (lastIndex + 1).toString());
								let allCartItems: QuickEntryData[] = [
									...data,
									newlyAddedDataObject,
								];
								if (updateShoppingCart) {
									updateShoppingCart();
								}

								callBack && callBack();
								handleOnSuccessAddItem(allCartItems);

								/* eslint-disable-next-line */
						  }
				);

				// Send a GA event with data about the item added to the cart
				// Sent as soon as the add is started, before and regardless of the API response.
				const { materialMaster, fastPath, category, form } = selectedProduct;
				const addToCartGAEventParams = {
					items: [
						{
							item_id: materialMaster, // SKU of the item
							item_name: fastPath ?? "",
							index: 0, // it is the only item being added to the cart
							item_category: category as string, // typecasting as category should always be defined
							item_category2: form as string, // typecasting as form should always be defined
							item_variant: qtyUOM,
							quantity: Number(quantity),
						} as EventParamsItem, // casting as location_id is required in type but not in payload
					],
				} as EventParams; // casting as currency and value aren't required in payload but are required in type

				// As of 06/27/2024, there is only the requirement to fire the GA event
				// from the cart -> quick entry flow
				sendGAEvent<EventParams>(
					GoogleAnalyticsEventName.ADD_TO_CART,
					addToCartGAEventParams
				);
			} else if (dataType === QuickEntryDataType.ShoppingList) {
				if (isLoggedIn) {
					const requestBody = {
						zipCode: getZipCode(
							isLoggedIn,
							zipCode,
							selectedShipToAddress,
							customerPickup,
							salesPlant
						),

						items: [
							{
								shoppingListId: id,
								ryersonSku: selectedProduct.materialMaster,
								quantity: quantity,
								customerPartNumber: getCustomerPartNumber(
									selectedProduct.altPartNumber
								),
								quantityUom: qtyUOM,
								pricingUom: priceUOM,
								domesticRequired: false,
							} as ShoppingListProduct,
						],
					};
					addProductsToShoppingList(
						requestBody,
						id,
						isLoggedIn,
						axiosInstance,
						(response: any) => {
							if (response?.data) {
								const responsedCartData: ShoppingListItem[] = response.data.map(
									(item: any) => {
										return {
											...item,
											materialWidth: selectedProduct.materialWidth,
											materialLengthMin: selectedProduct.materialLengthMin,
											itemDescriptionLocalization:
												selectedProduct.itemDescriptionLocalization,
											fastPath: selectedProduct.fastPath,
										} as ShoppingListItem;
									}
								);
								let allShoppingListItems = [...data, ...responsedCartData];
								callBack && callBack();
								handleOnSuccessAddItem(allShoppingListItems);
							}
						}
					);
				} else {
					// Signed out: appends `{ mm, qty, quom, puom }` to the `addToShoppingList`
					// cookie (5 days, written by `useCookie` from `@ryerson/frontend.common`) and
					// adds a local row once the cookie reads back with the new length. Apart from
					// the unrendered `QuickEntry.tsx`, which does the same write-then-read-back,
					// no file reads that cookie, and list details render inside `ProtectedRoute`,
					// which sends a signed-out visitor to sign-in (`ProtectedRoute.tsx`).
					const currentShoppingListData = JSON.parse(
						getItem("addToShoppingList") || JSON.stringify([])
					);
					let finalData: any = [];
					if (currentShoppingListData.length > 0) {
						finalData = [...currentShoppingListData, addToObject];
					} else {
						finalData = new Array(addToObject);
					}

					setShoppingListData(finalData, 5);
					// Now check whether cookies are added or not
					const getShoppingListItems = JSON.parse(getItem("addToShoppingList"));

					if (getShoppingListItems.length === finalData.length) {
						sessionStorage.setItem("shoppingListLastIndex", (lastIndex + 1).toString());
						let allShoppingListItems = [...data, newlyAddedDataObject];
						callBack && callBack();
						handleOnSuccessAddItem(allShoppingListItems);
					}
				}
			} else if (dataType === QuickEntryDataType.QuoteList) {
				// Draft-quote line. The response body is the saved line and is appended as-is.
				// `addLineItemAPI` routes `onComplete` through `.then`, so the `throw` below for a
				// non-200 response lands in its `.catch` and reaches the failure callback.
				const itemNumberValue = 1;
				let requestBody = {
					quoteId: id,
					skuNumber: selectedProduct.materialMaster,
					itemNumber: itemNumber ? itemNumber + itemNumberValue : itemNumberValue,
					customerReferenceNumber: "",
					customerPartNumber: getCustomerPartNumber(selectedProduct?.altPartNumber),
					purchaseOrderNumber: "",
					quantity: quantity,
					quantityUom: qtyUOM,
					pricingUom: priceUOM,
					requestedDeliveryDate: "",
					domesticRequired: false,
					notes: "",
					product: null,
					form: selectedProduct.form,
					shape: selectedProduct.shape,
					finish: null,
					grade: "",
					width: 0,
					length: 0,
					thickness: 0,
					category: selectedProduct.category,
					cutFlag: selectedProduct.cutFlag,
					orderable: selectedProduct.orderable,
					fastPath: selectedProduct.fastPath,
					mageniumProductId: "",
					weightPerFoot: selectedProduct.weightPerFoot,
					weightPerPiece: selectedProduct.weightPerPiece,
					weightPerSquareFoot: selectedProduct.weightPerSquareFoot,
				};

				addLineItemAPI(
					axiosInstance,
					id,
					requestBody,
					(response) => {
						if (isAxiosError(response) || (response as AxiosResponse).status !== 200) {
							throw response;
						}
						const newItem = (response as AxiosResponse).data;
						let allItems = [...data, newItem];
						callBack && callBack();
						handleOnSuccessAddItem(allItems);
					},
					(err) => {
						callBack && callBack();
						handleOnFailureAddItem();
					}
				);
			}
		}
	};

	const handleOnFailureAddItem = () => {
		setSearchTerm("");
		setDisplaySearchTerm("");
		setItemAddSuccess(false);
		setItemAddFail(true);
	};

	/**
	 * Hands the new rows to the caller, clears the search and shows "added below". For the cart it
	 * also resets the page's and the e-commerce context's cart pricing to
	 * `ShoppingCart/ShoppingCart.model.ts` → `defaultPricingData` (empty values).
	 */
	const handleOnSuccessAddItem = (updateProducts: QuickEntryData[]) => {
		if (dataType === QuickEntryDataType.ShoppingCart) {
			//TODO: Remove this condition when shopping list has pricing data

			setPricingOfItems && setPricingOfItems(defaultPricingData);
			setCartPricingData(defaultPricingData);
		}
		setSearchTerm("");
		setData(updateProducts);
		setDisplaySearchTerm("");
		setItemAddSuccess(true);
		setItemAddFail(false);
	};

	const closeQuickEntry = () => {
		setShowQuickEntry(false);
		setQuantity(undefined);
		setSearchTerm("");
	};

	const quickEntryProps: QuickEntryProps = {
		searchInputContainerRef,
		showDropdown,
		setShowDropdown,
		searchResult,
		onClickSelectItem,
		isError,
		quantity,
		setQuantity,
		selectedProduct,
		qtyUOM,
		qtyUOMDropdown,
		priceUOM,
		priceUOMDropdown,
		onClickAddBelow,
		displaySearchTerm,
		onChangeText,
		searchTerm,
		setPriceUOM,
		showQuickEntry,
		closeQuickEntry,
		itemAddSuccess,
		itemAddFail,
		updateQuantityUom,
		isQuickEntrySubmitted,
		setIsQuickEntrySubmitted,
		handleValidationResponse,
		hasMounted,
		stateMessenger,
		isQuickEntryValid,
	};

	return (
		<>
			<div id="quick-entry">
				<Media lessThan="lg">
					<QuickEntryMobileV2 {...quickEntryProps} />
				</Media>
				<Media greaterThanOrEqual="lg">
					<QuickEntryDesktopV2 {...quickEntryProps} />
				</Media>
			</div>
		</>
	);
};

export default QuickEntryV2;
